import { Component, OnInit } from '@angular/core';
import { PageHeaderComponent } from '../../../shared/page-header/page-header.component';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PropertyManagementCompanyDataService } from '../../../../../services/property-management-company/property-manager-company-data.service';
import { DetailsCardContainerComponent } from '../../../shared/cards/details-card-container/details-card-container.component';
import { AnimatedDelayDirective, AnimatedDirective } from '../../../shared/directives/animated.directive';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import { FlexSpacerDirective } from '../../../shared/directives/flex-spacer.directive';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TeamMemberAddCardComponent, TeamMemberFormGroup, TeamMemberValue } from './team-member-add-card/team-member-add-card.component';
import { I18nPluralPipe, NgForOf } from '@angular/common';
import { AvatarBlockModule } from '../../../shared/avatar-block/avatar-block.module';
import { avatarInitials } from '../../../../../models/entities/base/avatar';
import { LegfiPhonePipeModule } from '../../../../../pipes/format/legfi-phone.module';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { MatIconModule } from '@angular/material/icon';
import { validateRecursive } from '../../../../../directives/validators/validate-recursive';
import { GrowlerService } from '../../../../../services/growler.service';
import { ControlMessagesModule } from '../../../../shared/control-messages/control-messages.module';
import { finalize } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PropertyManagementCompanyService } from '../../../../../services/property-management-company/property-management-company.service';
import { I18nMap } from '../../../../../models/entities/i18n-map';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RouterButtonValue } from '../../../../../models/entities/router-button-value';

export interface AddTeamMemberFormGroup
{
    admins: FormArray<FormGroup<TeamMemberFormGroup>>;
}

@UntilDestroy()
@Component({
    selector: 'app-team-member-add',
    templateUrl: './team-member-add.component.html',
    standalone: true,
    imports: [
        PageHeaderComponent,
        ReactiveFormsModule,
        DetailsCardContainerComponent,
        AnimatedDirective,
        AnimatedDelayDirective,
        MatCardModule,
        MatButtonModule,
        RouterLink,
        MatToolbarModule,
        FlexSpacerDirective,
        TeamMemberAddCardComponent,
        NgForOf,
        AvatarBlockModule,
        LegfiPhonePipeModule,
        ButtonDirective,
        MatIconModule,
        ControlMessagesModule,
        I18nPluralPipe,
        MatSlideToggleModule,
    ],
})
export class TeamMemberAddComponent implements OnInit
{
    formGroup: FormGroup<AddTeamMemberFormGroup> = this._fb.group({
        admins: this._fb.array<FormGroup<TeamMemberFormGroup>>([], Validators.required),
    });

    savingTeamMember = false;
    routerBackButton: RouterButtonValue;

    readonly organizationsMapping: I18nMap = {
        '=0': 'No organizations',
        '=1': '1 organization',
        other: '# organizations',
    };
    readonly initials = avatarInitials;

    constructor(
            private _fb: FormBuilder,
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _router: Router,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    get admins() {
        return this.formGroup.get('admins') as FormArray<FormGroup<TeamMemberFormGroup>>;
    }

    ngOnInit() {
        this.routerBackButton = {
            label: 'Back',
            route: `/pmc/${this.pmcId}/team`,
            ariaLabel: 'Go back to team',
        };
    }

    submit() {
        validateRecursive(this.admins);

        if (this.admins.invalid) {
            this._growler.oops('Please double check all required fields');
            return;
        }

        const adminContext = this.admins.controls.length === 1 ? 'Team member' : (this.admins.controls.length + ' Team members');
        const newAdminsRequest: { groupAdmins: TeamMemberValue[]; } = {
            groupAdmins: this.admins.getRawValue()
                    .filter((admin) => !!admin.isSelected)
                    .map((admin) => {
                return {
                    id: null,
                    fullName: admin.fullName,
                    email: admin.email,
                    role: admin.role,
                    organizations: admin.organizations.map((organization) => organization.id),
                    phone: admin.phone,
                    emailNotification: admin.emailNotification,
                };
            }),
        };

        if (newAdminsRequest.groupAdmins.length === 0) {
            this._growler.oops('Please double check all required fields');
            this.admins.setErrors({ required: true });
            return;
        }

        this.savingTeamMember = true;
        this._propertyManagementCompanyService.createPropertyManagementTeamMembers(this.pmcId, newAdminsRequest).pipe(
                finalize(() => this.savingTeamMember = false),
                untilDestroyed(this),
        ).subscribe({
            next: () => {
                this._growler.success('Success', `${adminContext} added`);
                return this._router.navigate([this.routerBackButton.route]);
            },
            error: () => this._growler.oops(`Unable to add ${adminContext}`),
        });
    }
}
